import {
  generateCarouselContent,
  generateSlideImage,
  IMAGE_MODEL_FLASH,
  type CarouselGenerationResult,
  type CarouselGenerationSaveOptions,
} from './geminiService';
import { AspectRatio, CarouselStyle, Language, UploadedDocument } from '../types';

export interface GenerationInput {
  prompt: string;
  document?: UploadedDocument;
  style: CarouselStyle;
  slideCount: number;
  modelName: string;
  avatarContext?: string;
  language: Language;
  aspectRatio?: AspectRatio;      // defaults to '4/5'
  globalImageStyle?: string;      // defaults to '' (uses service default)
  /**
   * If set, the server persists the generated carousel atomically with
   * generation. Guarantees the project survives refresh / tab close /
   * network drop mid-request. Chat-first flow uses this; legacy /create
   * omits it and keeps saving client-side.
   */
  save?: CarouselGenerationSaveOptions;
}

export type OrchestratorStage = 'text' | 'image';

export interface OrchestratorCallbacks {
  onTextStart: () => void;
  onTextReady: (result: CarouselGenerationResult) => void;
  onImageJobStart: (slideId: string) => void;
  onImageJobDone: (slideId: string, imageUrl: string) => void;
  onImageJobFailed: (slideId: string, error: Error) => void;
  onAllDone: () => void;
  onError: (stage: OrchestratorStage, error: Error) => void;
}

export interface OrchestratorHandle {
  cancel: () => void;
}

const DEFAULT_CONCURRENCY = Number(
  (import.meta as any).env?.VITE_IMAGE_JOB_CONCURRENCY ?? 2,
);

/**
 * Runs the full generation pipeline.
 *
 * Pipeline:
 *   1. generateCarouselContent (text + format + showImage normalization happen inside)
 *   2. For each slide with showImage=true AND a non-empty imagePrompt, enqueue
 *      generateSlideImage with bounded concurrency.
 *   3. Per-slide callbacks fire as jobs start/finish/fail.
 *   4. onAllDone fires once every image job has settled.
 *
 * Cancellation:
 *   - cancel() sets an aborted flag. In-flight fetches inside generateSlideImage
 *     cannot be aborted (the underlying API does not expose AbortSignal), so we
 *     drop their results by checking the flag before firing callbacks.
 *   - Pending queue items are skipped entirely once aborted.
 */
export function runGenerationPipeline(
  input: GenerationInput,
  cb: OrchestratorCallbacks,
  concurrency: number = DEFAULT_CONCURRENCY,
): OrchestratorHandle {
  let aborted = false;

  const run = async () => {
    cb.onTextStart();

    let result: CarouselGenerationResult;
    try {
      result = await generateCarouselContent(
        input.prompt,
        input.slideCount,
        input.modelName,
        input.document,
        input.language,
        input.avatarContext,
        input.save,
      );
    } catch (error) {
      if (aborted) return;
      cb.onError('text', error instanceof Error ? error : new Error(String(error)));
      return;
    }

    if (aborted) return;
    cb.onTextReady(result);

    const imageSlides = result.slides.filter(
      (slide) => slide.showImage && slide.imagePrompt && slide.imagePrompt.trim().length > 0,
    );
    if (imageSlides.length === 0) {
      cb.onAllDone();
      return;
    }

    const aspectRatio: AspectRatio = input.aspectRatio ?? '4/5';
    const globalStyle = input.globalImageStyle ?? '';

    await runQueue(imageSlides, concurrency, async (slide) => {
      if (aborted) return;
      cb.onImageJobStart(slide.id);
      try {
        const imageUrl = await withTimeout(
          generateSlideImage(
            slide.imagePrompt as string,
            aspectRatio,
            IMAGE_MODEL_FLASH,
            globalStyle,
          ),
          90_000,
          `Image job timed out after 90s for slide ${slide.id}`,
        );
        if (aborted) return;
        if (!imageUrl) {
          cb.onImageJobFailed(slide.id, new Error('Empty image URL returned'));
          return;
        }
        cb.onImageJobDone(slide.id, imageUrl);
      } catch (error) {
        if (aborted) return;
        cb.onImageJobFailed(
          slide.id,
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    });

    if (!aborted) cb.onAllDone();
  };

  void run();

  return {
    cancel: () => {
      aborted = true;
    },
  };
}

/**
 * Rejects `promise` after `ms` milliseconds if it hasn't settled.
 * The underlying fetch keeps running but we stop waiting for it, so stuck
 * image jobs surface as failures instead of hanging the UI forever.
 */
function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** Minimal bounded-concurrency worker pool. */
async function runQueue<T>(
  items: T[],
  concurrency: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  const queue = [...items];
  const workers: Promise<void>[] = [];
  const limit = Math.max(1, Math.min(concurrency, items.length));

  for (let i = 0; i < limit; i++) {
    workers.push(
      (async () => {
        while (queue.length > 0) {
          const item = queue.shift();
          if (!item) return;
          await worker(item);
        }
      })(),
    );
  }

  await Promise.all(workers);
}
